// Service for handling audit trail logging for chargeback protection
import { supabase } from '../lib/supabase'
import { PaymentAuditLog, UserActivityLog, CheckoutSnapshot, WebhookLog, EmailNotification, DeviceCorrelation, ServerScreenshot } from '../lib/supabase'

export class AuditService {
  private static sessionId: string = this.generateSessionId()

  // Generate a unique session ID for the current session
  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Get current session ID
  static getSessionId(): string {
    return this.sessionId
  }

  // Get client IP and User-Agent
  private static async getClientInfo(): Promise<{ ip: string; userAgent: string }> {
    try {
      // Try to get IP from various sources
      const ipResponse = await fetch('https://api.ipify.org?format=json')
      const ipData = await ipResponse.json()
      const ip = ipData.ip

      const userAgent = navigator.userAgent

      return { ip, userAgent }
    } catch (error) {
      console.warn('Could not fetch client IP:', error)
      return {
        ip: 'unknown',
        userAgent: navigator.userAgent
      }
    }
  }

  // Log payment audit information
  static async logPaymentAudit(data: {
    userId: string
    paymentId?: string
    pspTransactionId?: string
    amount?: number
    currency?: string
    paymentStatus?: string
    threeDsResult?: any
    authorizationCode?: string
    captureStatus?: string
  }): Promise<void> {
    try {
      const clientInfo = await this.getClientInfo()

      const auditData: Partial<PaymentAuditLog> = {
        user_id: data.userId,
        payment_id: data.paymentId,
        psp_transaction_id: data.pspTransactionId,
        amount: data.amount,
        currency: data.currency,
        payment_status: data.paymentStatus,
        session_id: this.sessionId,
        ip_address: clientInfo.ip,
        user_agent: clientInfo.userAgent,
        three_ds_result: data.threeDsResult,
        authorization_code: data.authorizationCode,
        capture_status: data.captureStatus
      }

      const { error } = await supabase
        .from('payment_audit_logs')
        .insert(auditData)

      if (error) {
        console.error('Error logging payment audit:', error)
      } else {
        console.log('Payment audit logged successfully')
      }
    } catch (error) {
      console.error('Error in logPaymentAudit:', error)
    }
  }

  // Log user activity events
  static async logUserActivity(data: {
    userId: string
    eventType: string
    paymentId?: string
    details?: any
  }): Promise<void> {
    try {
      const clientInfo = await this.getClientInfo()

      const activityData: Partial<UserActivityLog> = {
        user_id: data.userId,
        session_id: this.sessionId,
        event_type: data.eventType,
        ip_address: clientInfo.ip,
        user_agent: clientInfo.userAgent,
        payment_id: data.paymentId,
        details: data.details
      }

      const { error } = await supabase
        .from('user_activity_logs')
        .insert(activityData)

      if (error) {
        console.error('Error logging user activity:', error)
      }
    } catch (error) {
      console.error('Error in logUserActivity:', error)
    }
  }

  // Log checkout snapshot
  static async logCheckoutSnapshot(data: {
    userId: string
    checkoutHtml: string
    termsAccepted: boolean
  }): Promise<void> {
    try {
      // Generate SHA256 hash of the HTML content
      const hash = await this.generateSHA256(data.checkoutHtml)

      const snapshotData: Partial<CheckoutSnapshot> = {
        user_id: data.userId,
        session_id: this.sessionId,
        checkout_html: data.checkoutHtml,
        hash_sha256: hash,
        terms_accepted: data.termsAccepted
      }

      const { error } = await supabase
        .from('checkout_snapshots')
        .insert(snapshotData)

      if (error) {
        console.error('Error logging checkout snapshot:', error)
      } else {
        console.log('Checkout snapshot logged successfully')
      }
    } catch (error) {
      console.error('Error in logCheckoutSnapshot:', error)
    }
  }

  // Log webhook data (server-side)
  static async logWebhook(data: {
    payload: any
    headers: any
    signatureVerified?: boolean
    paymentId?: string
  }): Promise<void> {
    try {
      const webhookData: Partial<WebhookLog> = {
        webhook_payload: data.payload,
        headers: data.headers,
        signature_verified: data.signatureVerified || false,
        payment_id: data.paymentId
      }

      const { error } = await supabase
        .from('webhook_logs')
        .insert(webhookData)

      if (error) {
        console.error('Error logging webhook:', error)
      }
    } catch (error) {
      console.error('Error in logWebhook:', error)
    }
  }

  // Log email notification
  static async logEmailNotification(data: {
    userId: string
    emailType: string
    recipientEmail: string
    emailHtml: string
  }): Promise<void> {
    try {
      // Generate SHA256 hash of the email HTML
      const hash = await this.generateSHA256(data.emailHtml)

      const emailData: Partial<EmailNotification> = {
        user_id: data.userId,
        email_type: data.emailType,
        recipient_email: data.recipientEmail,
        email_html: data.emailHtml,
        hash_sha256: hash
      }

      const { error } = await supabase
        .from('email_notifications')
        .insert(emailData)

      if (error) {
        console.error('Error logging email notification:', error)
      }
    } catch (error) {
      console.error('Error in logEmailNotification:', error)
    }
  }

  // Create device correlation analysis
  static async createDeviceCorrelation(data: {
    userId: string
    paymentId?: string
    paymentIp?: string
    paymentUserAgent?: string
  }): Promise<void> {
    try {
      const clientInfo = await this.getClientInfo()

      // Calculate correlation score based on IP and User-Agent match
      let correlationScore = 0
      if (data.paymentIp === clientInfo.ip) correlationScore += 50
      if (data.paymentUserAgent === clientInfo.userAgent) correlationScore += 50

      const correlationData: Partial<DeviceCorrelation> = {
        user_id: data.userId,
        payment_id: data.paymentId,
        payment_ip: data.paymentIp,
        payment_user_agent: data.paymentUserAgent,
        activity_ip: clientInfo.ip,
        activity_user_agent: clientInfo.userAgent,
        correlation_score: correlationScore
      }

      const { error } = await supabase
        .from('device_correlation')
        .insert(correlationData)

      if (error) {
        console.error('Error creating device correlation:', error)
      }
    } catch (error) {
      console.error('Error in createDeviceCorrelation:', error)
    }
  }

  // Log server screenshot (for premium page access)
  static async logServerScreenshot(data: {
    userId: string
    paymentId?: string
    pageUrl: string
    screenshotData: string
  }): Promise<void> {
    try {
      // Generate SHA256 hash of the screenshot data
      const hash = await this.generateSHA256(data.screenshotData)

      const screenshotData: Partial<ServerScreenshot> = {
        user_id: data.userId,
        payment_id: data.paymentId,
        page_url: data.pageUrl,
        screenshot_data: data.screenshotData,
        hash_sha256: hash
      }

      const { error } = await supabase
        .from('server_screenshots')
        .insert(screenshotData)

      if (error) {
        console.error('Error logging server screenshot:', error)
      }
    } catch (error) {
      console.error('Error in logServerScreenshot:', error)
    }
  }

  // Generate SHA256 hash
  private static async generateSHA256(data: string): Promise<string> {
    try {
      const encoder = new TextEncoder()
      const dataBuffer = encoder.encode(data)
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    } catch (error) {
      console.warn('SHA256 generation failed:', error)
      return 'hash_generation_failed'
    }
  }

  // Get audit trail for a user (for chargeback defense)
  static async getAuditTrail(userId: string): Promise<{
    paymentLogs: PaymentAuditLog[]
    activityLogs: UserActivityLog[]
    checkoutSnapshots: CheckoutSnapshot[]
    emailNotifications: EmailNotification[]
    deviceCorrelations: DeviceCorrelation[]
    serverScreenshots: ServerScreenshot[]
  }> {
    try {
      const [
        paymentLogsResult,
        activityLogsResult,
        checkoutSnapshotsResult,
        emailNotificationsResult,
        deviceCorrelationsResult,
        serverScreenshotsResult
      ] = await Promise.all([
        supabase.from('payment_audit_logs').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('user_activity_logs').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('checkout_snapshots').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('email_notifications').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('device_correlation').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('server_screenshots').select('*').eq('user_id', userId).order('created_at', { ascending: false })
      ])

      return {
        paymentLogs: paymentLogsResult.data || [],
        activityLogs: activityLogsResult.data || [],
        checkoutSnapshots: checkoutSnapshotsResult.data || [],
        emailNotifications: emailNotificationsResult.data || [],
        deviceCorrelations: deviceCorrelationsResult.data || [],
        serverScreenshots: serverScreenshotsResult.data || []
      }
    } catch (error) {
      console.error('Error fetching audit trail:', error)
      return {
        paymentLogs: [],
        activityLogs: [],
        checkoutSnapshots: [],
        emailNotifications: [],
        deviceCorrelations: [],
        serverScreenshots: []
      }
    }
  }
}

export default AuditService
